import { DepartmentType, type WorkOrderGetByIdResponse } from '@/types/workOrder.types';

export interface VendorBillingPrefill {
  vendorId: string;
  vendorName: string;
  vendorContactId: string | null;
  vendorContactName: string | null;
  vendorContactEmail: string | null;
}

function joinName(first?: string | null, last?: string | null): string | null {
  const joined = [first, last].filter(Boolean).join(' ').trim();
  return joined.length > 0 ? joined : null;
}

/** The work-order get-by-id response may expose vendor link fields on the
 * `vendor` object (`vendorId`, `vendorContactId`, `department`) and/or nested
 * `vendor.vendorId`, `vendorContact.vendorContactId`. Helpers accept both shapes.
 */
function readVendorId(workOrder: WorkOrderGetByIdResponse | undefined): string | null {
  const link = workOrder?.vendor;
  if (!link) return null;
  return link.vendorId ?? link.vendor?.vendorId ?? null;
}

/**
 * Use when mapping embedded work orders (e.g. PE service save payload) — the link object may exist
 * while a naive `wo?.vendor` check misses nested `vendor.vendorId`.
 */
export function hasVendorAssignmentOnWorkOrder(wo: Pick<WorkOrderGetByIdResponse, 'vendor'> | undefined): boolean {
  return Boolean(readVendorId(wo as WorkOrderGetByIdResponse));
}

function readVendorContactId(workOrder: WorkOrderGetByIdResponse | undefined): string | null {
  const link = workOrder?.vendor;
  if (!link) return null;
  return link.vendorContactId ?? link.vendorContact?.vendorContactId ?? null;
}

/**
 * Eligible when the work order is Process Service with a vendor and contact;
 * vendor department may be National, International, Deputization, or UIDDA.
 */
export function isProcessBillingVendorEligible(workOrder: WorkOrderGetByIdResponse | undefined): boolean {
  if (!workOrder || workOrder.service?.type !== 'PROCESS_SERVICE') return false;
  if (!readVendorId(workOrder) || !readVendorContactId(workOrder)) return false;
  const dept = workOrder.vendor?.department;
  if (dept === undefined) return true;
  return (
    dept === DepartmentType.NATIONAL ||
    dept === DepartmentType.INTERNATIONAL ||
    dept === DepartmentType.DEPUTIZATION ||
    dept === DepartmentType.UIDDA
  );
}

export function getVendorPrefillForBilling(
  workOrder: WorkOrderGetByIdResponse | undefined
): VendorBillingPrefill | null {
  const vendorId = readVendorId(workOrder);
  if (!vendorId) return null;
  const vendorLink = workOrder?.vendor;
  const linkedVendor = vendorLink?.vendor;
  const linkedVendorContact = vendorLink?.vendorContact;
  const trimmedContactName = linkedVendorContact?.contactName?.trim();
  const contactName = linkedVendorContact
    ? trimmedContactName
      ? trimmedContactName
      : joinName(linkedVendorContact.firstName, linkedVendorContact.lastName)
    : null;
  return {
    vendorId,
    vendorName: linkedVendor?.name?.trim() ? linkedVendor.name.trim() : 'Vendor',
    vendorContactId: readVendorContactId(workOrder),
    vendorContactName: contactName ?? null,
    vendorContactEmail: linkedVendorContact?.email ?? null,
  };
}

export function affidavitNameForSlot(index: number): string {
  return index === 0 ? 'Affidavit' : `Affidavit ${index + 1}`;
}
